import { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate, useNavigate } from "react-router-dom";
import { initNativePush, initDeepLinks, hideNativeSplash } from "@/lib/native-features";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { BrandingProvider } from "@/contexts/BrandingContext";
import { CountryProvider } from "@/contexts/CountryContext";
import { PortalProvider, usePortal } from "@/contexts/PortalContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Landing page variations — lazy to keep initial bundle small
const LandingPage2 = lazy(() => import("./pages/LandingPage2"));
const LandingPage3 = lazy(() => import("./pages/LandingPage3"));
const LandingPage4 = lazy(() => import("./pages/LandingPage4"));
const LandingPage5 = lazy(() => import("./pages/LandingPage5"));
const LandingPage6 = lazy(() => import("./pages/LandingPage6"));
const LandingPage7 = lazy(() => import("./pages/LandingPage7"));
const LandingPage8 = lazy(() => import("./pages/LandingPage8"));
const LandingPage9 = lazy(() => import("./pages/LandingPage9"));
const LandingPage10 = lazy(() => import("./pages/LandingPage10"));
const LandingPage11 = lazy(() => import("./pages/LandingPage11"));
const LandingPage12 = lazy(() => import("./pages/LandingPage12"));

// Public pages — small, needed on first load
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AcceptInvitePage from "./pages/corporate/AcceptInvitePage";
import PendingApprovalPage from "./pages/PendingApprovalPage";
import ForbiddenPage from "./pages/ForbiddenPage";
import RoadmapPage from "./pages/RoadmapPage";
import LogoutPage from "./pages/LogoutPage";
import NotFound from "./pages/NotFound";
const MFAChallengePage = lazy(() => import("./pages/MFAChallengePage"));

// Admin — lazy so non-admin users never download this bundle
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminCorporates = lazy(() => import("./pages/admin/AdminCorporates"));
const AdminMerchants = lazy(() => import("./pages/admin/AdminMerchants"));
const AdminAgents = lazy(() => import("./pages/admin/AdminAgents"));
const AdminVouchers = lazy(() => import("./pages/admin/AdminVouchers"));
const AdminRedemptions = lazy(() => import("./pages/admin/AdminRedemptions"));
const AdminSettlements = lazy(() => import("./pages/admin/AdminSettlements"));
const AdminFraud = lazy(() => import("./pages/admin/AdminFraud"));
const AdminSettlementReconciliation = lazy(() => import("./pages/admin/AdminSettlementReconciliation"));
const AdminFeatureFlags = lazy(() => import("./pages/admin/AdminFeatureFlags"));
const AdminNotificationTemplates = lazy(() => import("./pages/admin/AdminNotificationTemplates"));
const AdminAuditLog = lazy(() => import("./pages/admin/AdminAuditLog"));
const AdminRoadmap = lazy(() => import("./pages/admin/AdminRoadmap"));
const AdminApprovals = lazy(() => import("./pages/admin/AdminApprovals"));
const AdminConsumers = lazy(() => import("./pages/admin/AdminConsumers"));
const AdminDevTools = lazy(() => import("./pages/admin/AdminDevTools"));
const AdminPlatformFees = lazy(() => import("./pages/admin/AdminPlatformFees"));
const AdminDisputes = lazy(() => import("./pages/admin/AdminDisputes"));
const AdminSupportHub = lazy(() => import("./pages/admin/AdminSupportHub"));
const AdminSupportTicketPage = lazy(() => import("./pages/admin/AdminSupportTicketPage"));
const AdminFeaturedMerchants = lazy(() => import("./pages/admin/AdminFeaturedMerchants"));
const AdminPromotions = lazy(() => import("./pages/admin/AdminPromotions"));
const AdminNFCCards = lazy(() => import("./pages/admin/AdminNFCCards"));
const AdminCountries = lazy(() => import("./pages/admin/AdminCountries"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminAnnouncements = lazy(() => import("./pages/admin/AdminAnnouncements"));
const AdminUserDetail = lazy(() => import("./pages/admin/AdminUserDetail"));
const AdminMerchantDetail = lazy(() => import("./pages/admin/AdminMerchantDetail"));
const AdminCorporateDetail = lazy(() => import("./pages/admin/AdminCorporateDetail"));
const AdminAgentDetail = lazy(() => import("./pages/admin/AdminAgentDetail"));
const WhatsAppBotConfig = lazy(() => import("./pages/admin/phase2/WhatsAppBotConfig"));
const USSDSessionManager = lazy(() => import("./pages/admin/phase2/USSDSessionManager"));
const LoyaltyProgramme = lazy(() => import("./pages/admin/phase2/LoyaltyProgramme"));
const AgentMarketplace = lazy(() => import("./pages/admin/phase2/AgentMarketplace"));
const MerchantMap = lazy(() => import("./pages/admin/phase2/MerchantMap"));
const AdminBranding = lazy(() => import("./pages/admin/AdminBranding"));
const AdminGeoZones = lazy(() => import("./pages/admin/AdminGeoZones"));
const AdminExchange = lazy(() => import("./pages/admin/AdminExchange"));
const AdminPrePrint = lazy(() => import("./pages/admin/AdminPrePrint"));

// Compliance — lazy
const ComplianceLayout = lazy(() => import("./pages/compliance/ComplianceLayout"));
const ComplianceSettings = lazy(() => import("./pages/compliance/ComplianceSettings"));
const ComplianceDashboard = lazy(() => import("./pages/compliance/ComplianceDashboard"));
const KYCQueue = lazy(() => import("./pages/compliance/KYCQueue"));
const FraudCases = lazy(() => import("./pages/compliance/FraudCases"));
const AMLScreening = lazy(() => import("./pages/compliance/AMLScreening"));
const ComplianceReports = lazy(() => import("./pages/compliance/ComplianceReports"));
const ComplianceSARFiling = lazy(() => import("./pages/compliance/ComplianceSARFiling"));
const ComplianceSupportQueue = lazy(() => import("./pages/compliance/ComplianceSupportQueue"));

// Corporate — lazy
const CorporateLayout = lazy(() => import("./pages/corporate/CorporateLayout"));
const CorporateDashboard = lazy(() => import("./pages/corporate/CorporateDashboard"));
const BuyVouchers = lazy(() => import("./pages/corporate/BuyVouchers"));
const FloatTopUp = lazy(() => import("./pages/corporate/FloatTopUp"));
const CorporateVouchers = lazy(() => import("./pages/corporate/CorporateVouchers"));
const CorporateRecipients = lazy(() => import("./pages/corporate/CorporateRecipients"));
const CorporateCampaigns = lazy(() => import("./pages/corporate/CorporateCampaigns"));
const CorporateIntegrations = lazy(() => import("./pages/corporate/CorporateIntegrations"));
const CorporateReports = lazy(() => import("./pages/corporate/CorporateReports"));
const CorporateSettings = lazy(() => import("./pages/corporate/CorporateSettings"));
const CorporateSupport = lazy(() => import("./pages/corporate/CorporateSupport"));
const CorporateSubscriptions = lazy(() => import("./pages/corporate/CorporateSubscriptions"));
const SplitVoucher = lazy(() => import("./pages/corporate/SplitVoucher"));
const CorporateEmployees = lazy(() => import("./pages/corporate/CorporateEmployees"));
const CorporateActivityLog = lazy(() => import("./pages/corporate/CorporateActivityLog"));
const CorporateTeam = lazy(() => import("./pages/corporate/CorporateTeam"));
const CorporateVoucherTemplates = lazy(() => import("./pages/corporate/CorporateVoucherTemplates"));
const CorporateExchange = lazy(() => import("./pages/corporate/CorporateExchange"));

// Merchant — lazy
const MerchantLayout = lazy(() => import("./pages/merchant/MerchantLayout"));
const MerchantDashboard = lazy(() => import("./pages/merchant/MerchantDashboard"));
const RedeemVoucher = lazy(() => import("./pages/merchant/RedeemVoucher"));
const MerchantTransactions = lazy(() => import("./pages/merchant/MerchantTransactions"));
const MerchantBranches = lazy(() => import("./pages/merchant/MerchantBranches"));
const MerchantSettlements = lazy(() => import("./pages/merchant/MerchantSettlements"));
const MerchantAnalytics = lazy(() => import("./pages/merchant/MerchantAnalytics"));
const MerchantSettings = lazy(() => import("./pages/merchant/MerchantSettings"));
const MerchantOnboarding = lazy(() => import("./pages/merchant/MerchantOnboarding"));
const MerchantActivityLog = lazy(() => import("./pages/merchant/MerchantActivityLog"));
const MerchantDisputes = lazy(() => import("./pages/merchant/MerchantDisputes"));
const MerchantSupport = lazy(() => import("./pages/merchant/MerchantSupport"));
const MerchantTerminals = lazy(() => import("./pages/merchant/MerchantTerminals"));
const MerchantVoucherTemplates = lazy(() => import("./pages/merchant/MerchantVoucherTemplates"));
const MerchantPromotions = lazy(() => import("./pages/merchant/MerchantPromotions"));
const ConsumerNFCCards = lazy(() => import("./pages/consumer/ConsumerNFCCards"));
const ConsumerSpendingInsights = lazy(() => import("./pages/consumer/ConsumerSpendingInsights"));

// Agent — lazy
const AgentLayout = lazy(() => import("./pages/agent/AgentLayout"));
const AgentDashboard = lazy(() => import("./pages/agent/AgentDashboard"));
const AgentSell = lazy(() => import("./pages/agent/AgentSell"));
const AgentSales = lazy(() => import("./pages/agent/AgentSales"));
const AgentCommissions = lazy(() => import("./pages/agent/AgentCommissions"));
const AgentKYC = lazy(() => import("./pages/agent/AgentKYC"));
const AgentFloat = lazy(() => import("./pages/agent/AgentFloat"));
const AgentTraining = lazy(() => import("./pages/agent/AgentTraining"));
const AgentSupport = lazy(() => import("./pages/agent/AgentSupport"));
const AgentLeaderboard = lazy(() => import("./pages/agent/AgentLeaderboard"));
const AgentPrePrint = lazy(() => import("./pages/agent/AgentPrePrint"));
const AgentSettings = lazy(() => import("./pages/agent/AgentSettings"));

// Consumer — lazy
const ConsumerLayout = lazy(() => import("./pages/consumer/ConsumerLayout"));
const ConsumerSettings = lazy(() => import("./pages/consumer/ConsumerSettings"));
const ConsumerDashboard = lazy(() => import("./pages/consumer/ConsumerDashboard"));
const ConsumerVouchers = lazy(() => import("./pages/consumer/ConsumerVouchers"));
const ConsumerHistory = lazy(() => import("./pages/consumer/ConsumerHistory"));
const ConsumerProfile = lazy(() => import("./pages/consumer/ConsumerProfile"));
const ConsumerRedeem = lazy(() => import("./pages/consumer/ConsumerRedeem"));
const ConsumerFindMerchants = lazy(() => import("./pages/consumer/ConsumerFindMerchants"));
const ConsumerGifting = lazy(() => import("./pages/consumer/ConsumerGifting"));
const ConsumerLoyalty = lazy(() => import("./pages/consumer/ConsumerLoyalty"));
const ConsumerNotifications = lazy(() => import("./pages/consumer/ConsumerNotifications"));
const ConsumerNotificationPrefs = lazy(() => import("./pages/consumer/ConsumerNotificationPrefs"));
const ConsumerMerchantMap = lazy(() => import("./pages/consumer/ConsumerMerchantMap"));
const ConsumerBuyVouchers = lazy(() => import("./pages/consumer/ConsumerBuyVouchers"));
const ConsumerActivityLog = lazy(() => import("./pages/consumer/ConsumerActivityLog"));
const ConsumerDisputes = lazy(() => import("./pages/consumer/ConsumerDisputes"));
const ConsumerSupport = lazy(() => import("./pages/consumer/ConsumerSupport"));
const ConsumerVoucherTemplates = lazy(() => import("./pages/consumer/ConsumerVoucherTemplates"));
const ConsumerSubscriptions = lazy(() => import("./pages/consumer/ConsumerSubscriptions"));

// Shared pages
const FeatureRequests = lazy(() => import("./pages/shared/FeatureRequests"));
const AdminFeatureRequests = lazy(() => import("./pages/admin/AdminFeatureRequests"));

const queryClient = new QueryClient();

// Redirects portal subdomains from "/" to "/login"; main domain shows landing page
const PortalRoot = () => {
  const { isPortalMode } = usePortal();
  return isPortalMode ? <Navigate to="/login" replace /> : <LandingPage />;
};

// Initialises native-only features (push, auth deep links). No-op on web.
const NativeBootstrap = () => {
  const navigate = useNavigate();
  useEffect(() => {
    hideNativeSplash();
    initNativePush();
    initDeepLinks((path) => navigate(path));
  }, [navigate]);
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
    <CountryProvider>
    <TooltipProvider>
      <Toaster />
      <BrowserRouter>
        <NativeBootstrap />
        <ErrorBoundary>
        <PortalProvider>
        <AuthProvider>
          <BrandingProvider>
          <Suspense fallback={<div className="h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>}>
          <Routes>
            {/* Public */}
            <Route path="/" element={<PortalRoot />} />
            <Route path="/landing-2" element={<LandingPage2 />} />
            <Route path="/landing-3" element={<LandingPage3 />} />
            <Route path="/landing-4" element={<LandingPage4 />} />
            <Route path="/landing-5" element={<LandingPage5 />} />
            <Route path="/landing-6" element={<LandingPage6 />} />
            <Route path="/landing-7" element={<LandingPage7 />} />
            <Route path="/landing-8" element={<LandingPage8 />} />
            <Route path="/landing-9" element={<LandingPage9 />} />
            <Route path="/landing-10" element={<LandingPage10 />} />
            <Route path="/landing-11" element={<LandingPage11 />} />
            <Route path="/landing-12" element={<LandingPage12 />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/register/:role" element={<RegisterPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/logout" element={<LogoutPage />} />
            <Route path="/roadmap" element={<RoadmapPage />} />
            <Route path="/mfa-challenge" element={<MFAChallengePage />} />
            <Route path="/app/pending-approval" element={<PendingApprovalPage />} />
            <Route path="/app/403" element={<ForbiddenPage />} />
            {/* Super Admin */}
            <Route path="/app/admin" element={<ProtectedRoute requiredRoles={["super_admin"]}><AdminLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="corporates" element={<AdminCorporates />} />
              <Route path="corporates/:id" element={<AdminCorporateDetail />} />
              <Route path="merchants" element={<AdminMerchants />} />
              <Route path="merchants/:id" element={<AdminMerchantDetail />} />
              <Route path="agents" element={<AdminAgents />} />
              <Route path="agents/:id" element={<AdminAgentDetail />} />
              <Route path="vouchers" element={<AdminVouchers />} />
              <Route path="redemptions" element={<AdminRedemptions />} />
              <Route path="settlements" element={<AdminSettlements />} />
              <Route path="fraud" element={<AdminFraud />} />
                            <Route path="settlement-reconciliation" element={<AdminSettlementReconciliation />} />
              <Route path="feature-flags" element={<AdminFeatureFlags />} />
              <Route path="notification-templates" element={<AdminNotificationTemplates />} />
              <Route path="audit-log" element={<AdminAuditLog />} />
              <Route path="roadmap" element={<AdminRoadmap />} />
              <Route path="approvals" element={<AdminApprovals />} />
              <Route path="consumers" element={<AdminConsumers />} />
              <Route path="consumers/:id" element={<AdminUserDetail />} />
              {/* Phase 2 */}
              <Route path="whatsapp-bot" element={<WhatsAppBotConfig />} />
              <Route path="ussd-manager" element={<USSDSessionManager />} />
              <Route path="loyalty" element={<LoyaltyProgramme />} />
              <Route path="agent-marketplace" element={<AgentMarketplace />} />
              <Route path="merchant-map" element={<MerchantMap />} />
              <Route path="platform-fees" element={<AdminPlatformFees />} />
              <Route path="disputes" element={<AdminDisputes />} />
              <Route path="support" element={<AdminSupportHub />} />
              <Route path="support/:ticketId" element={<AdminSupportTicketPage />} />
              <Route path="featured-merchants" element={<AdminFeaturedMerchants />} />
              <Route path="promotions" element={<AdminPromotions />} />
              <Route path="nfc-cards" element={<AdminNFCCards />} />
              <Route path="countries" element={<AdminCountries />} />
              <Route path="dev-tools" element={<AdminDevTools />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="announcements" element={<AdminAnnouncements />} />
              <Route path="feature-requests" element={<AdminFeatureRequests />} />
              <Route path="branding" element={<AdminBranding />} />
              <Route path="geo-zones" element={<AdminGeoZones />} />
              <Route path="exchange" element={<AdminExchange />} />
              <Route path="preprint" element={<AdminPrePrint />} />
            </Route>

            {/* Compliance */}
            <Route path="/app/compliance" element={<ProtectedRoute requiredRoles={["compliance"]}><ComplianceLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<ComplianceDashboard />} />
              <Route path="kyc-queue" element={<KYCQueue />} />
              <Route path="fraud-cases" element={<FraudCases />} />
              <Route path="aml-screening" element={<AMLScreening />} />
              <Route path="reports" element={<ComplianceReports />} />
              <Route path="sar-filing" element={<ComplianceSARFiling />} />
              <Route path="feature-requests" element={<FeatureRequests />} />
              <Route path="support" element={<ComplianceSupportQueue />} />
              <Route path="settings" element={<ComplianceSettings />} />
            </Route>

            {/* Corporate */}
            <Route path="/app/corporate" element={<ProtectedRoute requiredRoles={["corporate_admin", "corporate_user", "corporate_finance", "corporate_hr", "corporate_viewer"]}><CorporateLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<CorporateDashboard />} />
              <Route path="buy-vouchers" element={<BuyVouchers />} />
              <Route path="float-topup" element={<FloatTopUp />} />
              <Route path="vouchers" element={<CorporateVouchers />} />
              <Route path="recipients" element={<CorporateRecipients />} />
              <Route path="campaigns" element={<CorporateCampaigns />} />
              <Route path="subscriptions" element={<CorporateSubscriptions />} />
              <Route path="split-voucher" element={<SplitVoucher />} />
              <Route path="employees" element={<CorporateEmployees />} />
              <Route path="integrations" element={<CorporateIntegrations />} />
              <Route path="reports" element={<CorporateReports />} />
              <Route path="settings" element={<CorporateSettings />} />
              <Route path="activity" element={<CorporateActivityLog />} />
              <Route path="team" element={<CorporateTeam />} />
              <Route path="feature-requests" element={<FeatureRequests />} />
              <Route path="voucher-templates" element={<CorporateVoucherTemplates />} />
              <Route path="exchange" element={<CorporateExchange />} />
              <Route path="support" element={<CorporateSupport />} />
            </Route>

            {/* Merchant */}
            <Route path="/app/merchant" element={<ProtectedRoute requiredRoles={["merchant_admin", "merchant_staff"]}><MerchantLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<MerchantDashboard />} />
              <Route path="redeem" element={<RedeemVoucher />} />
              <Route path="transactions" element={<MerchantTransactions />} />
              <Route path="branches" element={<MerchantBranches />} />
              <Route path="settlements" element={<MerchantSettlements />} />
              <Route path="analytics" element={<MerchantAnalytics />} />
              <Route path="settings" element={<MerchantSettings />} />
              <Route path="onboarding" element={<MerchantOnboarding />} />
              <Route path="activity" element={<MerchantActivityLog />} />
              <Route path="disputes" element={<MerchantDisputes />} />
              <Route path="terminals" element={<MerchantTerminals />} />
              <Route path="feature-requests" element={<FeatureRequests />} />
              <Route path="voucher-templates" element={<MerchantVoucherTemplates />} />
              <Route path="promotions" element={<MerchantPromotions />} />
              <Route path="support" element={<MerchantSupport />} />
            </Route>

            {/* Agent */}
            <Route path="/app/agent" element={<ProtectedRoute requiredRoles={["agent"]}><AgentLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<AgentDashboard />} />
              <Route path="sell" element={<AgentSell />} />
              <Route path="sales" element={<AgentSales />} />
              <Route path="commissions" element={<AgentCommissions />} />
              <Route path="float" element={<AgentFloat />} />
              <Route path="kyc" element={<AgentKYC />} />
              <Route path="training" element={<AgentTraining />} />
              <Route path="leaderboard" element={<AgentLeaderboard />} />
              <Route path="preprint" element={<AgentPrePrint />} />
              <Route path="feature-requests" element={<FeatureRequests />} />
              <Route path="support" element={<AgentSupport />} />
              <Route path="settings" element={<AgentSettings />} />
            </Route>

            {/* Consumer */}
            <Route path="/app/consumer" element={<ProtectedRoute requiredRoles={["consumer"]}><ConsumerLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<ConsumerDashboard />} />
              <Route path="vouchers" element={<ConsumerVouchers />} />
              <Route path="redeem" element={<ConsumerRedeem />} />
              <Route path="find-merchants" element={<ConsumerFindMerchants />} />
              <Route path="buy" element={<ConsumerBuyVouchers />} />
              <Route path="gift" element={<ConsumerGifting />} />
              <Route path="loyalty" element={<ConsumerLoyalty />} />
              <Route path="map" element={<ConsumerMerchantMap />} />
              <Route path="notifications" element={<ConsumerNotifications />} />
              <Route path="notification-preferences" element={<ConsumerNotificationPrefs />} />
              <Route path="history" element={<ConsumerHistory />} />
              <Route path="profile" element={<ConsumerProfile />} />
              <Route path="activity" element={<ConsumerActivityLog />} />
              <Route path="disputes" element={<ConsumerDisputes />} />
              <Route path="support" element={<ConsumerSupport />} />
              <Route path="feature-requests" element={<FeatureRequests />} />
              <Route path="card-style" element={<ConsumerVoucherTemplates />} />
              <Route path="subscriptions" element={<ConsumerSubscriptions />} />
              <Route path="nfc-cards" element={<ConsumerNFCCards />} />
              <Route path="insights" element={<ConsumerSpendingInsights />} />
              <Route path="settings" element={<ConsumerSettings />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          </BrandingProvider>
        </AuthProvider>
        </PortalProvider>
        </ErrorBoundary>
      </BrowserRouter>
    </TooltipProvider>
    </CountryProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
